import { Button, Image, UIHeading1, UIHeading2, useDeviceType, useUserBrowserTheme } from '@cian/ui-kit';
import cn from 'clsx';
import React, { useCallback, useState } from 'react';

import { PageIndicator } from '../PageIndicator';
import { PROGRAM_GUIDE_PAGES } from './pages';
import styles from './styles.css';

export interface IProgramGuideModalContentProps {
  onClose: () => void;
}

export const ProgramGuideModalContent: React.FC<IProgramGuideModalContentProps> = ({ onClose }) => {
  const theme = useUserBrowserTheme();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const [activePageIndex, setActivePageIndex] = useState(0);
  const activePage = PROGRAM_GUIDE_PAGES[activePageIndex];
  const isLastPage = activePageIndex === PROGRAM_GUIDE_PAGES.length - 1;
  const imageUrl = theme === 'dark' ? activePage.image.dark : activePage.image.light;
  const imageHeight = isDesktop ? 242 : 160;
  const MainHeading = isDesktop ? UIHeading1 : UIHeading2;

  const handleNextClick = useCallback(() => {
    if (isLastPage) {
      onClose();

      return;
    }

    setActivePageIndex(prev => prev + 1);
  }, [isLastPage, onClose]);

  const handleBackClick = useCallback(() => {
    setActivePageIndex(prev => Math.max(0, prev - 1));
  }, []);

  return (
    <div className={cn(styles.container, isDesktop && styles.desktop)} data-testid="program-guide-modal">
      <div className={styles.content}>
        <MainHeading fontWeight="bold">Как выбрать программу</MainHeading>

        {imageUrl ? (
          <div className={styles.image}>
            <Image
              src={imageUrl}
              alt={activePage.title}
              width="100%"
              height={imageHeight}
              borderRadius={'16px'}
              objectFit="cover"
              preloader
            />
          </div>
        ) : (
          <div className={styles['image-placeholder']} aria-hidden />
        )}

        <div className={styles['page-title']}>
          <UIHeading2 fontWeight="bold">{activePage.title}</UIHeading2>
        </div>

        <div className={styles.description}>{activePage.description}</div>
      </div>

      {isDesktop ? (
        <div className={styles['footer-desktop']}>
          <div className={styles['footer-side']}>
            {activePageIndex > 0 && (
              <Button
                theme="fill_secondary"
                size="L"
                onClick={handleBackClick}
                data-testid="program-guide-modal-back-button"
              >
                Назад
              </Button>
            )}
          </div>
          <PageIndicator count={PROGRAM_GUIDE_PAGES.length} activeIndex={activePageIndex} />
          <div className={styles['footer-side-right']}>
            <Button size="L" onClick={handleNextClick} data-testid="program-guide-modal-button">
              {isLastPage ? 'Закрыть' : 'Далее'}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.footer}>
          <PageIndicator count={PROGRAM_GUIDE_PAGES.length} activeIndex={activePageIndex} />
          <Button fullWidth size="L" onClick={handleNextClick} data-testid="program-guide-modal-button">
            {isLastPage ? 'Закрыть' : 'Далее'}
          </Button>
        </div>
      )}
    </div>
  );
};

ProgramGuideModalContent.displayName = 'ProgramGuideModalContent';
